import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { FaCalendarAlt, FaUser, FaTag, FaSearch, FaChevronRight, FaTimes, FaArrowRight, FaExternalLinkAlt } from 'react-icons/fa';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const articlesRef = useRef(null);
  
  // On component mount, check if we need to restore scroll position
  useEffect(() => {
    // Check if returning from a blog post (state contains clickedPostId)
    if ((location.state?.clickedPostId || sessionStorage.getItem('blogClickedPostId')) && articlesRef.current) {
      // Get the clicked post ID
      const clickedPostId = location.state?.clickedPostId || 
                           parseInt(sessionStorage.getItem('blogClickedPostId'), 10);
      
      console.log("Need to scroll to post ID:", clickedPostId);
      
      // Wait for page to be fully loaded and rendered
      const handleLoad = () => {
        // Use a moderate timeout for balance between speed and reliable rendering
        setTimeout(() => {
          console.log("Window loaded, attempting to find card with ID:", clickedPostId);
          
          // Try multiple selectors to find the card
          let cardElement = null;
          
          // Try data-post-id attribute
          cardElement = document.querySelector(`[data-post-id="${clickedPostId}"]`);
          console.log("Query selector result:", cardElement);
          
          // If not found, try all cards
          if (!cardElement) {
            const cards = document.querySelectorAll('.article-card');
            console.log("Found article cards:", cards.length);
            
            // Look through all cards to find one with matching ID
            cards.forEach(card => {
              const cardId = card.getAttribute('data-post-id');
              console.log("Card data-post-id:", cardId);
              if (cardId == clickedPostId) {
                cardElement = card;
              }
            });
          }
          
          // If card was found, scroll to it
          if (cardElement) {
            console.log("Card found, scrolling to it");
            const cardRect = cardElement.getBoundingClientRect();
            const cardTop = cardRect.top + window.scrollY;
            
            // Use smooth scrolling for better UX
            window.scrollTo({
              top: cardTop - 120, // Offset to position card nicely
              behavior: 'smooth' // Back to smooth scrolling for better UX
            });
          } else {
            console.log("Card not found, scrolling to articles section");
            // Fallback to articles section
            const articlesTop = articlesRef.current.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
              top: articlesTop,
              behavior: 'smooth' // Back to smooth scrolling for better UX
            });
          }
          
          // Clear the session storage to prevent unwanted scrolling on refresh
          sessionStorage.removeItem('blogClickedPostId');
        }, 400); // Moderate delay - balance between speed and reliability
      };
      
      // Execute with a small initial delay
      setTimeout(handleLoad, 100);
      
      return () => {
        // Cleanup not needed
      };
    }
  }, [location]);

  // Save scroll position before navigating to blog post
  const saveScrollPosition = (postId) => {
    // Store information about which card was clicked
    sessionStorage.setItem('blogClickedPostId', postId);
    
    // Navigate to the blog post
    navigate(`/blog/${postId}`, { 
      state: { 
        clickedPostId: postId
      } 
    });
  };

  // Detect mobile screen
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Blog data with sample posts - wrap in useMemo to avoid recreation on each render
  const blogPosts = useMemo(() => [
    {
      id: 1,
      title: "5 Key Benefits of Professional Demolition Services",
      date: "November 15, 2023",
      author: "Michael Johnson",
      category: "Demolition",
      excerpt: "Professional demolition offers numerous advantages over DIY approaches. Learn how expert demolition services can save time, ensure safety, and provide cost-effective solutions for your project.",
      image: "/images/blog/5_Key_Benefits_of_Professional_Demolition_Service_f21b1622-b0ac-41fe-97b1-d48fcab095a5_3.jpg",
      featured: true
    },
    {
      id: 2,
      title: "Preparing Your Property for Site Clearing: A Complete Guide",
      date: "October 22, 2023",
      author: "Sarah Thompson",
      category: "Site Clearing",
      excerpt: "Proper preparation before site clearing can save you time and money. This guide covers everything you need to do before the site clearing team arrives, from permits to utility marking.",
      image: "/images/services/realistic_scene_showing_a_skid_steer_loader.jpg",
      featured: false
    },
    {
      id: 3,
      title: "Environmentally Friendly Demolition Practices",
      date: "September 8, 2023",
      author: "Robert Miller",
      category: "Demolition",
      excerpt: "Modern demolition isn't just about tearing things down. Learn how eco-friendly demolition practices are reducing landfill waste, recycling materials, and minimizing environmental impact.",
      image: "/images/blog/a_picture_of_the_world_with_a_smile_cartoonish_--_f135712f-de6a-45bc-86cb-7c36bbea67b5_1.jpg",
      featured: true
    },
    {
      id: 4,
      title: "The Complete Guide to Concrete Removal",
      date: "August 17, 2023",
      author: "Jennifer Davis",
      category: "Concrete Removal",
      excerpt: "Removing concrete requires specialized equipment and expertise. This guide explains the different methods of concrete removal, from jackhammering to concrete sawing and beyond.",
      image: "/images/blog/a_picture_of_some_concrete_--v_6.1_a2ed6a0a-4083-440c-9f45-737a9544787c_3.jpeg",
      featured: false
    },
    {
      id: 5,
      title: "How to Choose the Right Excavation Company",
      date: "July 25, 2023",
      author: "David Wilson",
      category: "Excavation",
      excerpt: "Selecting the right excavation contractor is crucial for project success. Discover the key factors to consider, questions to ask, and red flags to watch for when hiring an excavation company.",
      image: "/images/blog/how_to_choose_the_right_excavation_company_--v_6._da974dfb-b524-4c90-9e23-2ba925737c0e_2.jpg",
      featured: false
    }
  ], []);

  // Categories derived from blog posts
  const categories = useMemo(() => 
    ['all', ...new Set(blogPosts.map(post => post.category))], 
  [blogPosts]);
  
  // Get featured posts
  const featuredPosts = useMemo(() => 
    blogPosts.filter(post => post.featured), 
  [blogPosts]);
  
  // Initialize filtered posts with all blog posts
  const [filteredPosts, setFilteredPosts] = useState(blogPosts);

  // Filter posts based on search term and category
  useEffect(() => {
    setIsLoading(true);
    let filtered = blogPosts;
    
    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter(post => post.category === activeCategory);
    }
    
    // Filter by search term
    if (searchTerm.trim() !== '') {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(search) || 
        post.excerpt.toLowerCase().includes(search) || 
        post.author.toLowerCase().includes(search) || 
        post.category.toLowerCase().includes(search)
      );
    }

    // Short timeout to show loading state for better UX
    setTimeout(() => {
      setFilteredPosts(filtered);
      setIsLoading(false);
    }, 300);
  }, [searchTerm, activeCategory, blogPosts]);

  // Show loading spinner only on initial page load
  useEffect(() => {
    // Set loading to false after a short delay on initial page load
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Clear search handler
  const handleClearSearch = () => {
    setSearchTerm('');
  };
  
  // Handle card click to show modal
  const handleCardClick = (e, post) => {
    e.preventDefault(); // Prevent default Link behavior
    setSelectedPost(post);
    setShowModal(true);
    // Add body class to prevent scrolling when modal is open
    document.body.classList.add('modal-open');
  };
  
  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedPost(null);
    // Remove body class to enable scrolling again
    document.body.classList.remove('modal-open');
  };
  
  // Navigate to full article
  const goToFullArticle = () => {
    if (selectedPost) {
      closeModal();
      saveScrollPosition(selectedPost.id);
    }
  };
  
  // Close modal when clicking escape key
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && showModal) {
        closeModal();
      }
    };
    
    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [showModal]);
  
  return (
    <>
      <Helmet>
        <title>Blog | Florida's Top Site Service & Demolition</title>
        <meta name="description" content="Read our latest articles about demolition, site clearing, excavation, and more. Stay informed with industry insights and helpful guides." />
      </Helmet>
      
      <div style={{
        height: '70vh',
        minHeight: '500px',
        background: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(\'/images/hero-bg.jpg\') center/cover no-repeat',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
      }}>
        {/* Radial gradient overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at center, rgba(255, 140, 0, 0.2) 0%, rgba(0, 0, 0, 0) 70%)'
        }} />
        
        {/* Content */}
        <div style={{
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '10px',
          }}>
            <h1 style={{
              color: 'white',
              fontSize: isMobile ? '3.2rem' : '4.5rem',
              marginBottom: '1.5rem',
              textShadow: '0 2px 5px rgba(0, 0, 0, 0.5)'
            }}>
              Our Blog
            </h1>
            
            <p style={{
              fontSize: isMobile ? '1.6rem' : '2rem',
              color: 'white',
              marginBottom: '2.5rem'
            }}>
              Expert insights, tips, and news about demolition and site clearing
            </p>
          </div>
        </div>
      </div>
      
      <MainContainer>
        {/* Search and Filter Section */}
        <SearchSection>
          <div className="container">
            <h2>Demolition & Site Clearing Articles</h2>
            <div className="search-filter-wrapper">
              <div className="search-container">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search for articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="clear-button" onClick={handleClearSearch}>
                    <FaTimes />
                  </button>
                )}
              </div>
              
              <div className="categories-filter">
                {categories.map((category, index) => (
                  <CategoryButton
                    key={index}
                    active={activeCategory === category}
                    onClick={() => setActiveCategory(category)}
                  >
                    {category === 'all' ? 'All Posts' : category}
                  </CategoryButton>
                ))}
              </div>
            </div>
          </div>
        </SearchSection>
        
        {/* Articles Grid Section */}
        <ArticlesSection ref={articlesRef}>
          <div className="container">
            {isLoading ? (
              <LoadingContainer>
                <div className="spinner"></div>
                <p>Loading articles...</p>
              </LoadingContainer>
            ) : (
              <>
                {filteredPosts.length > 0 ? (
                  <ArticlesGrid>
                    {filteredPosts.map((post) => (
                      <ArticleCard 
                        key={post.id} 
                        as="div" 
                        onClick={(e) => handleCardClick(e, post)} 
                        featured={post.featured}
                        data-post-id={post.id}
                        className="article-card"
                      >
                        <div className="image-wrapper">
                          <img src={post.image} alt={post.title} />
                        </div>
                        <div className="content">
                          <div className="meta-top">
                            {post.featured && <FeaturedTag>Featured</FeaturedTag>}
                            <CategoryTag>{post.category}</CategoryTag>
                            <div className="date">
                              <FaCalendarAlt />
                              <span>{post.date}</span>
                            </div>
                          </div>
                          
                          <h3>{post.title}</h3>
                          <p>{post.excerpt}</p>
                          
                          <div className="footer">
                            <div className="author">
                              <FaUser />
                              <span>{post.author}</span>
                            </div>
                            <ReadMoreButton>
                              View Preview <FaChevronRight />
                            </ReadMoreButton>
                          </div>
                        </div>
                      </ArticleCard>
                    ))}
                  </ArticlesGrid>
                ) : (
                  <NoResults>
                    <h3>No articles found</h3>
                    <p>Try adjusting your search or filter to find what you're looking for.</p>
                    <button onClick={() => {
                      setSearchTerm('');
                      setActiveCategory('all');
                    }}>
                      View All Posts
                    </button>
                  </NoResults>
                )}
              </>
            )}
          </div>
        </ArticlesSection>
      </MainContainer>
      
      {/* Modal for blog post preview */}
      {showModal && selectedPost && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalCloseButton onClick={closeModal} />
            
            <ModalImage>
              <img src={selectedPost.image} alt={selectedPost.title} />
              <CategoryTag className="category">{selectedPost.category}</CategoryTag>
              {selectedPost.featured && <FeaturedTag className="featured">Featured</FeaturedTag>}
            </ModalImage>
            
            <ModalBody>
              <ModalTitle>{selectedPost.title}</ModalTitle>
              
              <ModalMeta>
                <MetaItem>
                  <FaCalendarAlt />
                  <span>{selectedPost.date}</span>
                </MetaItem>
                <MetaItem>
                  <FaUser />
                  <span>{selectedPost.author}</span>
                </MetaItem>
              </ModalMeta>
              
              <ModalExcerpt>{selectedPost.excerpt}</ModalExcerpt>
              
              <ModalActions>
                <CloseButton onClick={closeModal}>
                  Close
                </CloseButton>
                <ReadArticleButton onClick={goToFullArticle}>
                  Read Full Article <FaArrowRight />
                </ReadArticleButton>
              </ModalActions>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

// Styled Components
const MainContainer = styled.main`
  background-color: #f8f9fa;
`;

const SearchSection = styled.section`
  padding: 6rem 0 3rem;
  background-color: white;
  border-bottom: 1px solid #eaeaea;
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }
  
  h2 {
    font-size: 3rem;
    font-weight: 700;
    color: #333;
    margin-bottom: 3rem;
    text-align: center;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 3px;
      background-color: var(--primary-color);
    }
  }
  
  .search-filter-wrapper {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
    max-width: 1000px;
    margin: 0 auto;
  }
  
  .search-container {
    position: relative;
    width: 100%;
    
    input {
      width: 100%;
      padding: 1.6rem 5rem 1.6rem 5rem;
      border: 1px solid #e0e0e0;
      border-radius: 50px;
      font-size: 1.6rem;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
      outline: none;
      transition: all 0.3s ease;
      
      &:focus {
        border-color: var(--primary-color);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
      }
    }
    
    .search-icon {
      position: absolute;
      left: 2rem;
      top: 50%;
      transform: translateY(-50%);
      color: #999;
      font-size: 1.8rem;
    }
    
    .clear-button {
      position: absolute;
      right: 2rem;
      top: 50%;
      transform: translateY(-50%);
      background: transparent;
      border: none;
      color: #999;
      font-size: 1.6rem;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.5rem;
      border-radius: 50%;
      transition: all 0.2s ease;
      
      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
        color: #666;
      }
    }
  }
  
  .categories-filter {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1rem;
    background-color: #f9f9f9;
    padding: 1.5rem;
    border-radius: 1rem;
    border: 1px solid #eee;
  }
  
  @media (max-width: 768px) {
    padding: 4rem 0 2rem;
    
    h2 {
      font-size: 2.4rem;
      margin-bottom: 2.5rem;
    }
  }
`;

const CategoryButton = styled.button`
  padding: 1rem 2rem;
  background-color: ${props => props.active ? 'var(--primary-color)' : '#f0f0f0'};
  color: ${props => props.active ? 'white' : '#333'};
  border: 1px solid ${props => props.active ? 'var(--primary-color)' : '#ddd'};
  border-radius: 50px;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin: 0.5rem;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-color)' : '#e0e0e0'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const ArticlesSection = styled.section`
  padding: 5rem 0 7rem;
  background-color: #f8f9fa;
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }
  
  @media (max-width: 768px) {
    padding: 3rem 0 5rem;
  }
`;

const ArticlesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(370px, 1fr));
  gap: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ArticleCard = styled.div.attrs(props => ({
  'data-post-id': props['data-post-id'] || '',
  className: `article-card ${props.className || ''}`
}))`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.06);
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  position: relative;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
  
  .image-wrapper {
    position: relative;
    height: 240px;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s ease;
    }
  }
  
  &:hover .image-wrapper img {
    transform: scale(1.05);
  }
  
  .content {
    padding: 2.4rem;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    
    .meta-top {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 1.2rem;
      margin-bottom: 1.8rem;
      
      .date {
        display: flex;
        align-items: center;
        font-size: 1.3rem;
        color: #666;
        
        svg {
          margin-right: 0.6rem;
          color: var(--primary-color);
        }
      }
    }
    
    h3 {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 1.2rem;
      line-height: 1.3;
      color: #222;
    }
    
    p {
      color: #555;
      line-height: 1.6;
      margin-bottom: 2rem;
      flex-grow: 1;
    }
    
    .footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: auto;
      
      .author {
        display: flex;
        align-items: center;
        font-size: 1.4rem;
        color: #666;
        
        svg {
          margin-right: 0.8rem;
          color: var(--primary-color);
        }
      }
    }
  }
`;

const CategoryTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const FeaturedTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #2c3e50;
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const ReadMoreButton = styled.div`
  display: inline-flex;
  align-items: center;
  color: var(--primary-color);
  font-weight: 700;
  font-size: 1.4rem;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 0.6rem;
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(3px);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 0;
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-left-color: var(--primary-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 2rem;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  p {
    font-size: 1.6rem;
    color: #777;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 5rem 0;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  
  h3 {
    font-size: 2.4rem;
    margin-bottom: 1.5rem;
    color: #333;
  }
  
  p {
    font-size: 1.6rem;
    color: #777;
    margin-bottom: 2.5rem;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
  
  button {
    padding: 1.2rem 2.5rem;
    background-color: var(--primary-color);
    color: white;
    border: none;
    border-radius: 50px;
    font-size: 1.6rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    }
  }
`;

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
  overflow-y: auto;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  animation: fadeIn 0.3s ease;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  
  @keyframes slideUp {
    from { transform: translateY(50px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  animation: slideUp 0.4s ease;
  
  @media (max-width: 768px) {
    max-height: 85vh;
  }
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  
  &:after {
    content: "×";
    font-size: 32px;
    font-weight: bold;
    line-height: 32px;
  }
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

const ModalImage = styled.div`
  width: 100%;
  height: 300px;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .category {
    position: absolute;
    bottom: 20px;
    left: 20px;
  }
  
  .featured {
    position: absolute;
    bottom: 20px;
    left: 140px;
  }
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const ModalBody = styled.div`
  padding: 2.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 2.6rem;
  font-weight: 700;
  color: #222;
  margin-bottom: 1.5rem;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const ModalMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-bottom: 2rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  color: #666;
  font-size: 1.4rem;
  
  svg {
    margin-right: 0.8rem;
    color: var(--primary-color);
  }
`;

const ModalExcerpt = styled.p`
  font-size: 1.6rem;
  line-height: 1.7;
  color: #444;
  margin-bottom: 3rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const CloseButton = styled.button`
  padding: 1.2rem 2.5rem;
  background: #f1f1f1;
  color: #444;
  border: 1px solid #ddd;
  border-radius: 50px;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e5e5e5;
  }
  
  @media (max-width: 576px) {
    width: 100%;
    order: 2;
  }
`;

const ReadArticleButton = styled.button`
  padding: 1.2rem 2.5rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    margin-left: 1rem;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background: #e07e00;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    
    svg {
      transform: translateX(3px);
    }
  }
  
  @media (max-width: 576px) {
    width: 100%;
    order: 1;
  }
`;

export default Blog; 